import { useUserRoles, type UserRolesResult } from '../../hooks/useUserRoles';
import Loading from './Loading';
import NoAccess, { type NoAccessReason } from './NoAccess';

/**
 * Fleet operator roles besides superadmins. Mirrors FLEET_READ_ROLES in
 * services/api-gateway/routes/fleet_admin.py; the gateway enforces them.
 */
export const FLEET_CONSOLE_ROLES = ['platform-admin', 'fleet-admin', 'fleet_admin', 'fleet-viewer', 'fleet_viewer'];

type RoleCheck = (roles: UserRolesResult) => boolean;

export const canUseSuperadminPages: RoleCheck = ({ isSuperuser }) => isSuperuser;
export const canUseTenantAdminPages: RoleCheck = ({ isTenantAdmin }) => isTenantAdmin;
export const canUseFleetConsole: RoleCheck = ({ isSuperuser, hasAnyRole }) =>
  isSuperuser || hasAnyRole(FLEET_CONSOLE_ROLES);
/**
 * Who may use the console at all (owner decision D22): tenant administration
 * (admin, tenant_admin) and platform operations (superadmin tier,
 * platform-admin, fleet operators). Every other tenant role works in the
 * portal; the portal offers the console link to the same roles
 * (frontend/src/lib/authorization.ts canAccessAdmin).
 */
export const canUseConsole: RoleCheck = (roles) => roles.isTenantAdmin || canUseFleetConsole(roles);
/** Mirrors CUSTOMER_FLEET_ROLES in services/api-gateway/routes/fleet_customer.py. */
export const canUseTenantFleetView: RoleCheck = ({ isTenantAdmin, hasAnyRole }) =>
  isTenantAdmin || hasAnyRole(['fleet-admin', 'fleet_admin']);
/**
 * Superadmins, and fleet operators the customer view refuses (fleet-viewer),
 * belong in the fleet console instead of /fleet-status.
 */
export const prefersFleetConsole: RoleCheck = (roles) =>
  roles.isSuperuser || (!canUseTenantFleetView(roles) && canUseFleetConsole(roles));
/** Mirrors ADMIN_ROLES and AUDITOR_ROLES in services/api-gateway/routes/enablement_pathways.py. */
export const canUseEnablementPages: RoleCheck = ({ isTenantAdmin, hasAnyRole }) =>
  isTenantAdmin || hasAnyRole(['compliance_officer', 'auditor', 'owner']);
/**
 * Mirrors is_audit_reader_request in services/api-gateway/routes/admin.py:
 * who may open the unredacted view. In the console only tenant administration
 * reaches the page (D22); the auditor reads the log in the tenant portal.
 */
export const canUseAuditLogs: RoleCheck = ({ isTenantAdmin, hasAnyRole }) =>
  isTenantAdmin || hasAnyRole(['auditor']);
/** Mirrors READ_ROLES in services/executive-cockpit-service/middleware/roles.py (D5). */
export const canUseExecutiveCockpit: RoleCheck = ({ isTenantAdmin, hasAnyRole }) =>
  isTenantAdmin || hasAnyRole(['executive', 'management']);

/**
 * Renders the page only for roles that can use it. Everyone else gets an
 * explicit "Keine Berechtigung" page at the same URL instead of a silent
 * redirect; the server enforces the same rule on every request.
 */
export function RoleRoute({
  allow,
  reason,
  children,
}: {
  allow: RoleCheck;
  reason: NoAccessReason;
  children: JSX.Element;
}) {
  const roles = useUserRoles();
  if (roles.isLoading) return <Loading />;
  if (!allow(roles)) return <NoAccess reason={reason} />;
  return children;
}

export function SuperadminRoute({ children }: { children: JSX.Element }) {
  return (
    <RoleRoute allow={canUseSuperadminPages} reason="superadmin">
      {children}
    </RoleRoute>
  );
}

export function TenantAdminRoute({ children }: { children: JSX.Element }) {
  return (
    <RoleRoute allow={canUseTenantAdminPages} reason="tenantAdmin">
      {children}
    </RoleRoute>
  );
}
